import * as React from "react";
import * as math from 'mathjs';
import * as _ from "lodash";
import * as ReactDOM from "react-dom";
import * as d3 from "d3";
import * as $ from "jquery";
import * as Rx from "rxjs";
import * as Util from "Util";
import * as Sauce from "Sauce";
import * as Validation from "Sauce/Validation";
import { Position2, ParametricCurve2 } from "Math/Geometry";
import { ChartMargins } from "UI/UIConstants";
import { Plotter, IdCurve } from "UI/Math/Plotter";
import { SimpleParametricCurve2 } from "Math/ParametricCurve";
import { AnimationHelper } from "UI/AnimationHelper";
import { merge, makeDomId } from "Util";
import { NumberInput, TextAreaInput, Checkbox } from "Sauce/Form";
import { Real, RoundingInteger, CheckboxValue } from "Sauce/Validation/Basic";

require("./Oscilloscope.scss");

interface _OscilloscopeProps {
    xmin: number;
    xmax: number;
    tmin: number;
    tmax: number;
    ymin: number;
    ymax: number;
    numSamples: number;
    duration: number;
    repeat: boolean;
    repeatDelay: number;
    autoplay: boolean;
}
export type OscilloscopeProps = _OscilloscopeProps & {
    curve: IdCurve | null;
}

type OscilloscopeControllerProps = {
    [T in keyof _OscilloscopeProps]: Validation.ValueSource;
} & {
    flush: (newProps: OscilloscopeProps) => void;
    startx: string; // initialize formuals to this
    starty: string;  // initialize formulas to this
    repeatDelay: number;
}

interface OState { 
    tend: number, 
    ycurve: IdCurve | null, 
    xcurve: IdCurve | null
};

type fn = { (t: number): number; }

interface CState {
    formula_x: Validation.ParseResult<fn>,
    formula_y: Validation.ParseResult<fn>,
    samples: Validation.ParseResult<number>,
    duration: Validation.ParseResult<number>,
    repeat: Validation.ParseResult<boolean>,
    repeatDelay: number,
    tmin: Validation.ParseResult<number>,
    tmax: Validation.ParseResult<number>,
    xmin: Validation.ParseResult<number>,
    xmax: Validation.ParseResult<number>,
    ymin: Validation.ParseResult<number>,
    ymax: Validation.ParseResult<number>,
}

let prev_curve_id = "";

class FormulaInput extends TextAreaInput<fn> {}
export class OscilloscopeController<SK extends keyof CState> extends Sauce.Component<OscilloscopeControllerProps, CState> {
    private xfn: fn;
    private yfn: fn;
    private formulaProcessorFactory = new Validation.ValueProcessorFactory(
        formatted => this.compile(formatted),
        undefined,
        undefined,
        undefined,
        raw => raw.replace(/\n\r?/, "")
    );
    private curve_id(x: Validation.ParseResult<fn>, y: Validation.ParseResult<fn>): string {
        return makeDomId(x.formatted + "___" + y.formatted);
    }
    
    private formula_validator = this.formulaProcessorFactory.make();
    private samp_validator = new RoundingInteger(50, 10000).make();
    private duration_validator = new RoundingInteger(0, 1000 * 60 * 15).make();
    private t_min_validator = new Real(4, -100 * Math.PI, 100 * Math.PI).make();
    private t_max_validator = new Real(4, -100 * Math.PI, 100 * Math.PI).make();
    private bool_validator = new CheckboxValue().make();
    private real_validator = new Real().make();

    private combine_curves(x: Validation.ParseResult<fn>, y: Validation.ParseResult<fn>): IdCurve | null {
        if (x.value && y.value) {
            let curve = new SimpleParametricCurve2(x.value, y.value);
            let idc: IdCurve = { 
                key: this.curve_id(x, y),
                value: curve
            };
            return idc;
        }
        return null;
    }

    private collateState(partial: Partial<CState>): CState {
        return Object.assign({}, this.state, partial);
    }

    private collateProps(state: CState): OscilloscopeProps {
        let props: OscilloscopeProps = {} as any;
        for(let key of Object.keys(state)) {
            let val = (state as any)[key];
            if (key === "curve") {}
            else if (val instanceof Validation.ParseResult) {
                (props as any)[key] = val.value;
            }
            else {
                (props as any)[key] = val;
            }
        } 
        let curve = this.combine_curves(state.formula_x, state.formula_y);
        if (curve && curve.key !== prev_curve_id) {
            props.curve = curve;
        }
        return props;
    }
    private all_valid(state: CState): boolean {
        for(let key of Object.keys(state)) {
            let val = (state as any)[key];
            if (val instanceof Validation.ParseResult && !val.valid) return false;
        }
        return true;
    }
    private flush = (partial: Partial<CState>) => {
        let state = this.collateState(partial);
        if (this.all_valid(state)) {
            let props = this.collateProps(state);
            this.props.flush(props);
        }
    }
    private propagate(partial: Partial<CState>) {
        this.setState(partial);
        this.flush(this.collateState(partial));
    }

    constructor(props: OscilloscopeControllerProps) {

        super(props);
        /* ordinarily, we just give text strings to the individual input components and let them parse the initial
            as if the user had typed it in. However, this widget has more logic associated with upwards propagation
            and so it needs these parse results before the component even mounts */
        this.state = {
            formula_x: this.formula_validator(props.startx),
            formula_y: this.formula_validator(props.starty),
            samples: this.samp_validator(props.numSamples),
            duration: this.duration_validator(props.duration),
            repeat: this.bool_validator(props.repeat),
            repeatDelay: props.repeatDelay,
            tmin: this.t_min_validator(props.tmin),
            tmax: this.t_max_validator(props.tmax),
            xmin: this.real_validator(props.xmin),
            xmax: this.real_validator(props.xmax),
            ymin: this.real_validator(props.ymin),
            ymax: this.real_validator(props.ymax)
        };
    }
    componentWillMount() {
        let props = this.collateState({});
        this.flush(props);
    }

    private compile(input: string): fn {
        let compile = math.parse(input).compile() 
        return (t:number) => compile.eval({t: t});
    }
    render() { 
        return <div className="geometroy-oscilloscope-controller">
            <div className="x-formula">
                <label>x signal function</label>
                <FormulaInput 
                    value={this.state.formula_x}
                    processor={this.formula_validator}
                    flush={val => this.propagate({ formula_x: val })}
                />
                <label className="error-message">{this.state.formula_x.errorMsg}</label>
            </div>
            <div className="y-formula">
                <label>y signal function</label>
                <FormulaInput 
                    value={this.state.formula_y}
                    processor={this.formula_validator}
                    flush={val => this.propagate({ formula_y: val }) }/>
                <label className="error-message">{this.state.formula_y.errorMsg}</label>
            </div>
            <div className="repeat">
                <label>Repeat</label>
                <Checkbox initialValue={this.state.repeat}
                        processor={new Validation.ValueProcessorFactory<boolean>().make()}
                        flush={val => this.propagate({ repeat: val })} />
            </div>
            <div className="duration">
                <label>Animation Duration (ms)</label>
                <NumberInput value={this.state.duration}
                    processor={this.duration_validator}
                    flush={val => this.propagate({ duration: val })} />
                <label className="error-message" content={this.state.duration.errorMsg}/>
            </div>
            <div className="samples">
                <label>How often should we sample the curve</label>
                <NumberInput value={this.state.samples}
                    processor={this.samp_validator}
                    flush={val => this.propagate({ samples: val })} />
                <label className="error-message" value={this.state.samples.errorMsg}/>
            </div>
            <div className="t-min">
                <label>tmin: start animating at tmin</label>
                <NumberInput value={this.state.tmin}
                    processor={this.real_validator}
                    flush={val => this.propagate({ tmin: val })} />
                <label className="error-message" value={this.state.tmin.errorMsg}/>
            </div>
            <div className="t-max">
                <label>tmax: stop animating at tmax</label>
                <NumberInput value={this.state.tmax}
                        processor={this.real_validator}
                        flush={val => this.propagate({ tmax: val })} />
                <label className="error-message" value={this.state.tmax.errorMsg}/>
            </div>
            <div className="x-min">
                <label>x min</label>
                 <NumberInput value={this.state.xmin}
                        processor={this.real_validator}
                        flush={val => this.propagate({ xmin: val })} />
                <label className="error-message" value={this.state.xmin.errorMsg}/>
            </div>
            <div className="x-max">
                <label>x max</label>
                 <NumberInput value={this.state.xmax}
                        processor={this.real_validator}
                        flush={val => this.propagate({ xmax: val })} />
                <label className="error-message" value={this.state.xmax.errorMsg}/>
            </div>
            <div className="y-min">
                <label>y min</label>
                 <NumberInput value={this.state.ymin}
                        processor={this.real_validator}
                        flush={val => this.propagate({ ymin: val })} />
                <label className="error-message" value={this.state.ymin.errorMsg}/>
            </div>
            <div className="y-max">
                <label>y max</label>
                 <NumberInput
                        value={this.state.ymax}
                        processor={this.real_validator}
                        flush={val => this.propagate({ ymax: val })} />
                <label className="error-message" value={this.state.ymax.errorMsg}/>
            </div>
        </div>
    }
}

export class Oscilloscope extends Sauce.Component<OscilloscopeProps, OState> {
    private numSamples: number = 100;
    private margins = ChartMargins;
    private clockSub: Rx.Subscription;
    private pauser: Rx.Subject<boolean>;
    private paused: boolean;
    private ah = new AnimationHelper(3000, 60);
    
    constructor(props: OscilloscopeProps) {
        super(props)
        this.paused = !props.autoplay;
        this.pauser = new Rx.BehaviorSubject<boolean>(this.paused);

        this.state = { 
            tend: this.props.tmin, 
            ycurve: this.makeycurve(this.props.curve), 
            xcurve: this.makexcurve(this.props.curve) 
        };
        this.togglePause = this.togglePause.bind(this);
    }

    private getClock(props: OscilloscopeProps): Rx.Observable<number> {
        return new AnimationHelper(props.duration, 60).simpleTicker(props.tmin, 
                                    props.tmax,
                                    props.repeat,
                                    props.repeatDelay,
                                    this.pauser);
    }

    private startAnimating(clock: Rx.Observable<number>): Rx.Subscription {
        return this.subscribe(clock, t => {
            this.setState({ tend: t });
        })
    }

    private restartAnimation(props: OscilloscopeProps) {
        this.remove(this.clockSub);
        this.clockSub = this.startAnimating(this.getClock(props));
    }

    componentDidMount() {
        this.clockSub = this.startAnimating(this.getClock(this.props));
    }

    animation_parameters_changed(prev: OscilloscopeProps, next: OscilloscopeProps): boolean {
        let oldcurve = prev.curve && prev.curve.key;
        let newcurve = next.curve && next.curve.key;
        
        return prev.duration !== next.duration
                || prev.repeat !== next.repeat
                || prev.repeatDelay !== next.repeatDelay
                || prev.tmin !== next.tmin
                || prev.tmax !== next.tmax
                || prev.repeat !== next.repeat
                || oldcurve !== newcurve;
    }

    componentWillReceiveProps(newProps: OscilloscopeProps) {
        let oldcurve = this.props.curve && this.props.curve.key;
        let newcurve = newProps.curve && newProps.curve.key;

        if (oldcurve !== newcurve) {
            this.setState(merge({
                    ycurve: this.makeycurve(newProps.curve), 
                    xcurve: this.makexcurve(newProps.curve)
                }, this.state));
        }

        if (this.animation_parameters_changed(this.props, newProps)) {
            this.restartAnimation(newProps);
        }
    }
    
    makeycurve(mainCurve: IdCurve | null): IdCurve | null {
        if (mainCurve != undefined) {
            return { key: "ycurve" + mainCurve.key, value: new SimpleParametricCurve2(t => t, t => mainCurve.value.y(t)) };
        }
        return null;
    }

    makexcurve(mainCurve: IdCurve | null): IdCurve | null {
        if (mainCurve != undefined) {
            return { key: "xcurve" + mainCurve.key, value: new SimpleParametricCurve2(t => mainCurve.value.x(t), t => t) };
        }
        return null;
    }

    togglePause() {
        this.paused = !this.paused;
        this.pauser.next(this.paused);
    }

    render() {
        return <div className={"geometry-component-oscilloscope"} onClick={this.togglePause}>
            <div className={"y-curve"}>
                <Plotter curves={this.state.ycurve ? [this.state.ycurve] : []} 
                    tmin={this.props.tmin} 
                    tmax={this.state.tend} 
                    xmin={this.props.tmin} xmax={this.props.tmax} ymin={this.props.ymin} ymax={this.props.ymax} 
                    numSamples={this.props.numSamples}
                    traceHorizontal={true}/>
            </div>
            <div className={"x-curve"}>                
                <Plotter curves={this.state.xcurve ? [this.state.xcurve] : []} 
                    tmin={this.props.tmin} 
                    tmax={this.state.tend} 
                    xmin={this.props.xmin} xmax={this.props.xmax} ymin={this.props.tmin} ymax={this.props.tmax} 
                    numSamples={this.props.numSamples}
                    traceVertical={true}/>
            </div>
            <div className={"main-curve"}>
                <Plotter curves={this.props.curve ? [this.props.curve] : []}
                    tmin={this.props.tmin} 
                    tmax={this.state.tend} 
                    xmin={this.props.xmin} xmax={this.props.xmax} ymin={this.props.ymin} ymax={this.props.ymax} 
                    numSamples={this.props.numSamples * 5}
                    traceVertical={true}
                    traceHorizontal={true}/>
            </div>
        </div>
    }
}


// WEBPACK FOOTER //
// ./src/code/UI/Math/Oscilloscope.tsx